import { flip, shift, size } from '@floating-ui/core'
import { autoUpdate, useFloating } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { mergeProps } from '@react-aria/utils'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { Popover } from 'components/Popover/Popover'
import { cloneElement, forwardRef, ReactElement, ReactNode, useImperativeHandle } from 'react'
import { ContextMenuTriggerProps } from './Types'

export const ContextMenuTrigger = forwardRef(
  (
    {
      children,
      placement,
      onClose,
      popoverClassName,
      onOpenChange,
      containFocus = true,
      autoFocus = true,
      noOverlay,
      useSqueeze = true,
      maxHeight,
      autoUpdateOptions = {},
      shouldCloseOnBlur = false,
    }: ContextMenuTriggerProps,
    ref,
  ) => {
    let [trigger, content] = children
    if (!content) {
      ;[, content] = children as [ReactElement, (close: () => void) => ReactNode]
    }

    const overlayState = useOverlayTriggerState({ onOpenChange })

    const { x, y, floating, reference } = useFloating({
      strategy: 'fixed',
      placement,
      middleware: [
        flip(),
        shift({ padding: 4 }),
        size(
          useSqueeze
            ? {
                padding: 16,
                apply({ availableHeight, elements }) {
                  Object.assign(elements.floating.style, {
                    maxHeight: `${Math.max(96, maxHeight ?? availableHeight)}px`,
                  })
                },
              }
            : {},
        ),
      ],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
          ...autoUpdateOptions,
        })
      },
    })

    useImperativeHandle(ref, () => ({
      openMenu: (clientX: number, clientY: number) => {
        reference({
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            x: clientX,
            y: clientY,
            top: clientY,
            left: clientX,
            right: clientX,
            bottom: clientY,
          }),
        })
        overlayState.open()
      },
      closeMenu: () => {
        overlayState.close()
      },
    }))

    return (
      <>
        {cloneElement(
          trigger as ReactElement,
          mergeProps((trigger as ReactElement).props, {
            onContextMenu: (e: MouseEvent) => {
              if (overlayState.isOpen) {
                overlayState.close()
              }
              e.stopPropagation()
              e.preventDefault()
              const { clientX, clientY } = e
              reference({
                getBoundingClientRect() {
                  return {
                    width: 0,
                    height: 0,
                    x: clientX,
                    y: clientY,
                    top: clientY,
                    left: clientX,
                    right: clientX,
                    bottom: clientY,
                  }
                },
              })
              overlayState.open()
            },
          }),
        )}
        {overlayState.isOpen && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={{ position: 'fixed', top: y || 0, left: x || 0 }}
              className={popoverClassName}
              isOpen={overlayState.isOpen}
              shouldCloseOnInteractOutside={(element) => {
                if (!element) {
                  return false
                }
                return !element.closest('.ignore-click-outside')
              }}
              onClose={() => {
                onClose?.()
                overlayState.close()
              }}
              ref={floating}
              isDismissable
              shouldCloseOnBlur={shouldCloseOnBlur}
              containFocus={containFocus}
              autoFocus={autoFocus}
              noOverlay={noOverlay}
            >
              {typeof content === 'function' ? content(overlayState.close) : content}
            </Popover>
          </FloatingPortal>
        )}
      </>
    )
  },
)
